import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, useLocation } from 'react-router-dom';
import routeNames from './routeNames';
import {
    Layout,
    Dashboard,
    AffinityHub,
    DigitalPhysicianEngagement,
    FieldPhysicianEngagement,
    TotalPhysicianEngagement,
    WebMetrics,
    Triggers,
    Physicians,
    PhysicianAnalysis,
    Queries,
    Campaigns,
    DataSources,
    DataSourceHistory,
    Settings,
    TopPrescribers,
    AlwaysOn,
    Error,
    FileUpload,
    TriggersSummary,
    PaidMediaEngagement,
    PaidSearchEngagement,
    WebSiteEngagement,
    Creatives,
} from '../components/dashboard';
import { CompanyUserBasedTabs } from '../components/sidebar/constants';
import {
    handleHidePaidSearchButton,
    handleHideWebMetricsButton,
    handleShowAlwaysOnButton,
} from '../utility';
import { logout } from '../redux/thunks/userThunks';
import { showCreative } from '../constants';
import { pushPageView, pushUserLogin } from '../utility/gtm';

const Router = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const [userBasedShow, setUserBasedShow] = useState(false);
    const [isStaffBased, setIsStaffBased] = useState(true);
    const {
        userDetails: {
            is_admin = false,
            is_staff = 0,
            email,
            company: userCompany,
        },
    } = useSelector((state) => state.user);
    const { company: selectedCompany, isOmaniChannel } = useSelector(
        (state) => state.company,
    );

    const checkForInactivity = () => {
        const expireTime = localStorage.getItem('expireTime');
        if (expireTime && Number(expireTime) < Date.now()) {
            dispatch(logout())
                .then(() => {
                    window.location.href = '/login';
                })
                .catch((error) => console.error('Error in logout ', error));
        }
    };

    const updateExpireTime = () => {
        const expireTime = Date.now() + 20 * 60 * 1000; // 20 minutes in milliseconds
        localStorage.setItem('expireTime', expireTime.toString());
    };

    useEffect(() => {
        const interval = setInterval(checkForInactivity, 5000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        updateExpireTime();
        const events = ['click', 'keypress', 'mouseover', 'scroll'];
        events.forEach((event) =>
            window.addEventListener(event, updateExpireTime),
        );
        return () => {
            events.forEach((event) =>
                window.removeEventListener(event, updateExpireTime),
            );
        };
    }, []);

    useEffect(() => {
        const matchingTab = CompanyUserBasedTabs.find(
            (tab) => tab.company === selectedCompany.id && tab.user === email,
        );
        if (matchingTab) {
            setUserBasedShow(true);
        } else {
            setIsStaffBased(
                is_staff && userCompany?.id === selectedCompany?.id,
            );
        }
    }, [is_staff, selectedCompany.id]);

    useEffect(() => {
        pushPageView({
            page_path: location.pathname,
            is_admin,
            is_staff,
            company_id: selectedCompany?.id,
            channel: isOmaniChannel ? 'omani' : 'default',
        });
    }, [location.pathname]);
    useEffect(() => {
        if (email) {
            pushUserLogin({
                is_admin,
                email,
                company: selectedCompany,
            });
        }
    }, [email]);
    return (
        <Routes>
            <Route element={<Layout />}>
                <Route element={<Dashboard />} path={routeNames.DASHBOARD} />
                <Route
                    element={
                        is_admin || userBasedShow ? <AffinityHub /> : <Error />
                    }
                    path={routeNames.AFFINITY_HUB}
                />
                <Route
                    element={
                        isOmaniChannel ? (
                            <Error />
                        ) : (
                            <DigitalPhysicianEngagement />
                        )
                    }
                    path={routeNames.DIGITAL_PHYSICIAN_ENGAGEMENT}
                />
                <Route
                    element={
                        isOmaniChannel ? (
                            <Error />
                        ) : (
                            <DigitalPhysicianEngagement />
                        )
                    }
                    path={routeNames.DIGITAL_ENGAGEMENT}
                />
                <Route
                    element={
                        isOmaniChannel ? (
                            <Error />
                        ) : (
                            <FieldPhysicianEngagement />
                        )
                    }
                    path={routeNames.FIELD_PHYSICIAN_ENGAGEMENT}
                />
                <Route
                    element={
                        isOmaniChannel ? (
                            <Error />
                        ) : (
                            <TotalPhysicianEngagement />
                        )
                    }
                    path={routeNames.TOTAL_PHYSICIAN_ENGAGEMENT}
                />
                <Route
                    element={
                        isOmaniChannel ? <PaidMediaEngagement /> : <Error />
                    }
                    path={routeNames.PAID_MEDIA_ENGAGEMENT}
                />
                <Route
                    element={isOmaniChannel ? <WebSiteEngagement /> : <Error />}
                    path={routeNames.WEBSITE_ENGAGEMENT}
                />
                <Route
                    element={
                        isOmaniChannel ? <PaidSearchEngagement /> : <Error />
                    }
                    path={routeNames.PAID_SEARCH_ENGAGEMENT}
                />
                <Route
                    element={<TriggersSummary />}
                    path={routeNames.TRIGGERS_SUMMARY}
                />
                <Route
                    element={
                        is_admin ||
                        (userBasedShow && !isOmaniChannel) ||
                        !isOmaniChannel ? (
                            <Physicians />
                        ) : (
                            <Error />
                        )
                    }
                    path={routeNames.PHYSICIANS}
                />
                <Route
                    element={
                        is_admin ||
                        handleHideWebMetricsButton(selectedCompany?.id) ? (
                            <WebMetrics />
                        ) : (
                            <Error />
                        )
                    }
                    path={routeNames.WEB_METRICS}
                />
                <Route element={<Triggers />} path={routeNames.TRIGGER} />
                <Route
                    element={
                        is_admin || userBasedShow ? (
                            <PhysicianAnalysis />
                        ) : (
                            <Error />
                        )
                    }
                    path={routeNames.PHYSICIAN_ANALYSIS}
                />
                <Route
                    element={
                        is_admin || userBasedShow || !isStaffBased ? (
                            <Queries />
                        ) : (
                            <Error />
                        )
                    }
                    path={routeNames.QUERIES}
                />
                <Route
                    element={
                        is_admin || userBasedShow || !isStaffBased ? (
                            <Campaigns />
                        ) : (
                            <Error />
                        )
                    }
                    path={routeNames.CAMPAIGNS}
                />
                <Route
                    element={
                        is_admin || userBasedShow ? <DataSources /> : <Error />
                    }
                    path={routeNames.DATA_SOURCES}
                />
                <Route
                    element={
                        is_admin || userBasedShow ? (
                            <DataSourceHistory />
                        ) : (
                            <Error />
                        )
                    }
                    path={routeNames.DATA_SOURCES_HISTORY}
                />
                <Route element={<Settings />} path={routeNames.SETTINGS} />
                <Route
                    element={isOmaniChannel ? <Error /> : <TopPrescribers />}
                    path={routeNames.TOP_PRESCRIBERS}
                />
                <Route
                    element={
                        is_admin ||
                        handleShowAlwaysOnButton(selectedCompany?.id) ? (
                            <AlwaysOn />
                        ) : (
                            <Error />
                        )
                    }
                    path={routeNames.ALWAYS_ON}
                />
                <Route element={<Error />} path={routeNames.ERROR} />
                <Route element={<FileUpload />} path={routeNames.FILES} />
                <Route
                    element={showCreative ? <Creatives /> : <Error />}
                    path={routeNames.CREATIVES}
                />
                <Route
                    element={
                        is_admin ||
                        handleHidePaidSearchButton(selectedCompany?.id) ? (
                            <PaidSearchEngagement />
                        ) : (
                            <Error />
                        )
                    }
                    path={routeNames.PAID_SEARCH}
                />
            </Route>
        </Routes>
    );
};

export default Router;
